import { FlatfileProvider, useEvent, useFlatfile, useListener } from '@flatfile/react';
import React, { FC, useEffect } from 'react';
import api, { Flatfile } from '@flatfile/api';
import { RecordData } from '@flatfile/api/api';
import { Space, Workbook, Sheet } from '@flatfile/react';
import {
	FLATFILE_SUBMIT_ACTION_NAME,
	getFlatfileInputRecordsFromData,
	submitData,
	WORK_BOOK_SUBMIT_ACTION,
} from './faltfile_utils';
import { FLAT_FILE_PUBLISHABLE_KEY } from '../../utils/DataConstants';
import { nullSafeGet } from '../../utils/DataAccessUtils';

const FlatfileWorkbook: FC<any> = ({
	dataLoadComplete,
	entityName,
	type,
	fields,
	data,
	onComplete,
	onDataLoad,
	recordHook,
	validateRecordsOnSubmit,
}): React.ReactElement => {
	const { open, openPortal, closePortal } = useFlatfile({
		onClose: () => {
			console.log('FlatfileImporter Closed');
			onComplete && onComplete();
		},
	});

	const sheetConfig = {
		name: type,
		slug: type,
		access: ['add', 'edit', 'delete'],
		allowDelete: true,
		fields,
	};

	const workbook = {
		name: 'All Data',
		labels: ['pinned'],
		sheets: [sheetConfig],
		actions: [WORK_BOOK_SUBMIT_ACTION],
	};

	useEvent('job:ready', { job: `workbook:${FLATFILE_SUBMIT_ACTION_NAME}` }, async (event) => {
		await submitData(event, onDataLoad, onComplete, validateRecordsOnSubmit);
	});

	useListener((listener) => {
		listener.on('workbook:created', async (event) => {
			await api.jobs.create({
				type: 'workbook',
				operation: 'insert-records',
				source: event.context.workbookId,
				trigger: 'immediate',
				mode: 'foreground',
			});
		});

		listener.on('job:ready', { job: 'workbook:insert-records' }, async (event) => {
			const workbookId = event.context.workbookId;
			const workbook = await api.workbooks.get(workbookId);
			await api.jobs.ack(event.context.jobId, {
				progress: 10,
			});
			if (workbook.data.sheets) {
				const sheetId = nullSafeGet('data.sheets.0.id', workbook);
				const records = getFlatfileInputRecordsFromData(data) as RecordData[];
				try {
					await api.records.insert(sheetId, records);
				} catch (err) {
					console.log(err);
				}
			}

			await api.jobs.complete(event.context.jobId, {
				outcome: {
					acknowledge: false,
					message: `${entityName || type} loaded successfully!`,
				},
			});
		});
	});

	useEffect(() => {
		return () => {
			// Uncomment this in locahost inorder to test flatfile related features
			// if (process.env.NODE_ENV === 'production') {
			closePortal();
			// }
		};
	}, []);

	useEffect(() => {
		dataLoadComplete ? (!open ? openPortal() : (() => {})()) : closePortal();
	}, [closePortal, dataLoadComplete, open, openPortal]);

	return (
		<Space
			config={{
				metadata: {
					sidebarConfig: {
						showSidebar: false,
					},
				},
			}}
		>
			<Workbook
				config={workbook as Flatfile.workbooks.CreateWorkbookConfig}
				onRecordHooks={[
					[
						type,
						(record) => {
							return recordHook ? recordHook(record) : record;
						},
					],
				]}
			>
				<Sheet config={sheetConfig as Flatfile.sheets.SheetConfig} />
			</Workbook>
		</Space>
	);
};

const FlatfileImporter: FC<any> = ({ ...props }): React.ReactElement => {
	return (
		<FlatfileProvider
			publishableKey={FLAT_FILE_PUBLISHABLE_KEY}
			config={{
				preload: false,
			}}
		>
			<FlatfileWorkbook {...props} />
		</FlatfileProvider>
	);
};

export default FlatfileImporter;
